<p-confirmDialog
  [key]="deleteQuotaConfirmKey"
  appendTo="body"
  [baseZIndex]="12001"
  [style]="{ width: '28rem' }"
  styleClass="tokens-allocation-delete-confirm">
  <ng-template pTemplate="message">
    <div class="quota-delete-confirm">
      <p class="quota-delete-confirm__main m-0">
        Remove token allocation for <b>{{ deleteConfirmUserLabel }}</b>?
      </p>
      <p class="quota-delete-confirm__sub m-0">You can restore it from the Deleted allocations view.</p>
    </div>
  </ng-template>
</p-confirmDialog>

<p-drawer
  [visible]="visible()"
  (visibleChange)="onDrawerVisibleChange($event)"
  position="right"
  appendTo="body"
  styleClass="sidebarlist1 tokens-allocation-drawer"
  header="Tokens Allocation"
  [closable]="true">
  @if (drawerLoading()) {
    <div class="drawer-loading" aria-busy="true" aria-live="polite">
      <p-progressSpinner strokeWidth="4" [style]="{ width: '36px', height: '36px' }" />
      <p class="hint-text">Loading token allocation…</p>
    </div>
  } @else if (accessDenied()) {
    <p class="text-sm text-[var(--p-slate-600)]">
      Token allocation management is available to company administrators only.
    </p>
  } @else if (showUserMetrics()) {
    <section class="user-metrics-section">
      <div class="metrics-header-row">
        <button
          type="button"
          class="quota-icon-btn quota-icon-btn--text drawer-back-btn"
          pTooltip="Back to allocations"
          tooltipPosition="top"
          (click)="closeUserMetrics()">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <use xlink:href="assets/images/icon-library.svg#arrow-left"></use>
          </svg>
          <span>Back</span>
        </button>
        @if (metricsQuota(); as quota) {
          <div class="metrics-allocated-tokens">
            <span class="metrics-allocated-tokens__label">Allocated tokens</span>
            <span class="metrics-allocated-tokens__value">{{ formatTokens(quota.allocatedTokens) }}</span>
          </div>
        }
      </div>
      <h3 class="section-title">
        User Tokens Usage
        <span class="metrics-user-name">{{ metricsUserLabel() }}</span>
      </h3>
      @if (metricsQuota(); as quota) {
        @let usage = userUsage(quota);
        <div class="token-usage-card metrics-user-usage-card">
          <div
            class="token-usage-bar"
            role="progressbar"
            [attr.aria-valuenow]="usage.usedPercent"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.aria-label]="usage.usedPercent + '% consumed, ' + usage.remainingPercent + '% available'">
            <div
              class="token-usage-bar__fill"
              [class]="'token-usage-bar__fill--' + consumedBarTone(usage.usedPercent)"
              [style.width.%]="usage.usedPercent"></div>
          </div>
          <div class="token-usage-header">
            <div class="token-usage-stat consumed">
              <span class="token-usage-stat__value">{{ usage.usedPercent }}% Consumed</span>
              <span class="token-usage-stat__count">{{ formatTokens(quota.usedTokens) }}</span>
            </div>
            <div class="token-usage-stat available">
              <span class="token-usage-stat__value">{{ usage.remainingPercent }}% Available</span>
              <span class="token-usage-stat__count">{{ formatTokens(quota.remainingTokens) }}</span>
            </div>
          </div>
        </div>
      }

      

      <div class="usage-period-filters" role="group" aria-label="Token usage period">
        @for (opt of usagePeriodOptions; track opt.value) {
          <button
            type="button"
            class="usage-period-chip"
            [class.usage-period-chip--active]="usageMetricsPeriod() === opt.value"
            (click)="onUsagePeriodChange(opt.value)">
            {{ opt.label }}
          </button>
        }
      </div>

      @if (usageMetricsError()) {
        <p class="error-text">{{ usageMetricsError() }}</p>
      }

      @if (usageMetricsLoading()) {
        <div class="metrics-loading" aria-busy="true" aria-live="polite">
          <p-progressSpinner strokeWidth="4" [style]="{ width: '32px', height: '32px' }" />
          <p class="hint-text">Loading usage metrics…</p>
        </div>
      } @else if (usageMetrics(); as metrics) {
        <div class="usage-metrics-panels">
          <div class="usage-metrics-panel usage-metrics-panel--agents">
            <h4 class="usage-metrics-panel__title">Tokens by Agent</h4>
            <ul class="agent-usage-list">
              @for (agent of metrics.byAgent; track agent.agentCode) {
                <li class="agent-usage-item">
                  <span class="agent-usage-item__dot" [style.background-color]="agentChartColor(agent.agentCode)"></span>
                  <div class="agent-usage-item__body">
                    <div class="agent-usage-item__row">
                      <span class="agent-usage-item__name">{{ agent.agentName }}</span>
                      <span class="agent-usage-item__tokens">{{ formatTokens(agent.tokens) }}</span>
                    </div>
                    <div class="agent-usage-item__bar" aria-hidden="true">
                      <div
                        class="agent-usage-item__bar-fill"
                        [style.width.%]="agentBarWidth(agent, metrics.totalTokens)"
                        [style.background-color]="agentChartColor(agent.agentCode)"></div>
                    </div>
                  </div>
                </li>
              }
            </ul>
          </div>

          <div class="usage-metrics-panel usage-metrics-panel--total">
            <h4 class="usage-metrics-panel__title">Total Tokens</h4>
            <div class="usage-donut-wrap">
              @let donutSegments = usageDonutSegments(metrics);
              <div class="usage-donut">
                <svg class="usage-donut__svg" viewBox="0 0 100 100" aria-hidden="true">
                  @if (donutSegments.length) {
                    @for (seg of donutSegments; track seg.agentCode) {
                      <circle
                        class="usage-donut__segment"
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        [attr.stroke]="seg.color"
                        stroke-width="16"
                        [attr.stroke-dasharray]="seg.dashArray"
                        [attr.stroke-dashoffset]="seg.dashOffset"
                        transform="rotate(-90 50 50)"
                        [pTooltip]="seg.agentName"
                        tooltipPosition="top" />
                    }
                  } @else {
                    <circle
                      class="usage-donut__segment usage-donut__segment--empty"
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#e2e8f0"
                      stroke-width="16" />
                  }
                </svg>
                <div class="usage-donut__hole">
                  <span class="usage-donut__total">{{ formatTokens(metrics.totalTokens) }}</span>
                  <span class="usage-donut__label">Total tokens</span>
                </div>
              </div>
              <ul class="usage-donut-legend">
                @for (agent of metrics.byAgent; track agent.agentCode) {
                  <li class="usage-donut-legend__item">
                    <span class="usage-donut-legend__dot" [style.background-color]="agentChartColor(agent.agentCode)"></span>
                    <span class="usage-donut-legend__label">{{ agent.agentName }}</span>
                    <span class="usage-donut-legend__value">{{ formatTokens(agent.tokens) }}</span>
                  </li>
                }
              </ul>
            </div>
          </div>
        </div>
      }
    </section>
  } @else if (showActivities()) {
    <section class="activities-section">
      <button
        type="button"
        class="quota-icon-btn quota-icon-btn--text drawer-back-btn"
        pTooltip="Back to allocations"
        tooltipPosition="top"
        (click)="closeActivities()">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <use xlink:href="assets/images/icon-library.svg#arrow-left"></use>
        </svg>
        <span>Back</span>
      </button>

      <h3 class="section-title">Activities</h3>

      <div class="search-field-wrap activities-search">
        <input
          type="text"
          pInputText
          pSize="small"
          class="search-field-input"
          placeholder="Search activities"
          [ngModel]="activitySearchInput()"
          (ngModelChange)="activitySearchInput.set($event)"
          (keyup.enter)="canRunActivitySearch() && runActivitySearch()" />
        @if (hasActivitySearchText()) {
          <button
            type="button"
            class="search-field-icon-btn clear-btn"
            aria-label="Clear search"
            pTooltip="Clear search"
            tooltipPosition="top"
            (click)="clearActivitySearch()">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <use xlink:href="assets/images/icon-library.svg#x"></use>
            </svg>
          </button>
        }
        <button
          type="button"
          class="search-field-icon-btn search-btn"
          aria-label="Search activities"
          pTooltip="Search activities"
          tooltipPosition="top"
          [disabled]="!canRunActivitySearch()"
          (click)="runActivitySearch()">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <use xlink:href="assets/images/icon-library.svg#search"></use>
          </svg>
        </button>
      </div>

      @if (activitiesError()) {
        <p class="error-text">{{ activitiesError() }}</p>
      }

      <p-table
        [value]="activities()"
        [lazy]="true"
        [paginator]="true"
        [rows]="activitiesPageSize()"
        [first]="activitiesTableFirst()"
        [totalRecords]="activitiesTotal()"
        [loading]="activitiesLoading()"
        [rowsPerPageOptions]="[10, 20, 50]"
        (onLazyLoad)="onActivityPageChange($event)"
        dataKey="id"
        scrollable="true"
        scrollHeight="300px"
        styleClass="allocation-table activities-table">
        <ng-template pTemplate="header">
          <tr>
            <th>Description</th>
            <th class="datetime-col">Date &amp; time</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.description }}</td>
            <td class="datetime-col">{{ formatActivityDateTime(row.createdAt) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="2" class="empty-cell">No activities found.</td>
          </tr>
        </ng-template>
      </p-table>
    </section>
  } @else {
    <section class="company-tokens-section">
      @if (companySummary(); as s) {
        @let usage = companyUsage(s);
        <div class="company-tokens-header-row">
          <h3 class="section-title company-tokens-header-row__title">Company Tokens</h3>
          <div class="metrics-allocated-tokens">
            <span class="metrics-allocated-tokens__label">Allocated tokens</span>
            <span class="metrics-allocated-tokens__value">{{ formatTokens(s.allocatedTokens) }}</span>
          </div>
        </div>
        <div class="token-usage-card">
          <div
            class="token-usage-bar"
            role="progressbar"
            [attr.aria-valuenow]="usage.usedPercent"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.aria-label]="usage.usedPercent + '% consumed, ' + usage.remainingPercent + '% available'">
            <div
              class="token-usage-bar__fill"
              [class]="'token-usage-bar__fill--' + consumedBarTone(usage.usedPercent)"
              [style.width.%]="usage.usedPercent"></div>
          </div>
          <div class="token-usage-header">
            <div class="token-usage-stat consumed">
              <span class="token-usage-stat__value">{{ usage.usedPercent }}% Consumed</span>
              <span class="token-usage-stat__count">{{ formatTokens(s.usedTokens) }}</span>
            </div>
            <div class="token-usage-stat available">
              <span class="token-usage-stat__value">{{ usage.remainingPercent }}% Available</span>
              <span class="token-usage-stat__count">{{ formatTokens(s.remainingTokens) }}</span>
            </div>
          </div>
        </div>
      } @else {
        <h3 class="section-title">Company Tokens</h3>
        <p class="hint-text">No company token settings found.</p>
      }
    </section>

    <section class="user-quotas-section">
      <h3 class="section-title">User Tokens</h3>

      <div class="toolbar-row">
        <div class="toolbar-filters">
          <div class="search-field-wrap toolbar-search">
            <input
              type="text"
              pInputText
              pSize="small"
              class="search-field-input"
              placeholder="Search name or email"
              [ngModel]="searchInput()"
              (ngModelChange)="searchInput.set($event)"
              (keyup.enter)="runQuotaSearch()" />
            @if (hasQuotaSearchText()) {
              <button
                type="button"
                class="search-field-icon-btn clear-btn"
                aria-label="Clear search"
                pTooltip="Clear search"
                tooltipPosition="top"
                (click)="clearQuotaSearch()">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <use xlink:href="assets/images/icon-library.svg#x"></use>
                </svg>
              </button>
            }
            <button
              type="button"
              class="search-field-icon-btn search-btn"
              aria-label="Search user quotas"
              pTooltip="Search"
              tooltipPosition="top"
              (click)="runQuotaSearch()">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <use xlink:href="assets/images/icon-library.svg#search"></use>
              </svg>
            </button>
          </div>

          <p-select
            class="quota-status-filter"
            [options]="quotaListViewOptions"
            optionLabel="label"
            optionValue="value"
            [ngModel]="quotaListView()"
            (ngModelChange)="onQuotaListViewChange($event)"
            appendTo="body" />
        </div>

        <div class="toolbar-actions">
          <button
            type="button"
            class="quota-icon-btn quota-icon-btn--labeled"
            pTooltip="Activities"
            tooltipPosition="top"
            aria-label="Activities"
            (click)="openActivities()">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <use xlink:href="assets/images/icon-library.svg#history"></use>
            </svg>
            <span>Activities</span>
          </button>
          <button
            type="button"
            class="quota-icon-btn quota-icon-btn--labeled quota-icon-btn--primary"
            pTooltip="Assign tokens"
            tooltipPosition="top"
            aria-label="Assign tokens"
            (click)="openCreate()">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <use xlink:href="assets/images/icon-library.svg#user-plus"></use>
            </svg>
            <span>Assign</span>
          </button>
        </div>
      </div>

      @if (listError()) {
        <p class="error-text">{{ listError() }}</p>
      }

      @if (drawerDataLoaded()) {
        <p-table
          [value]="quotas()"
          [lazy]="true"
          [paginator]="true"
          [rows]="pageSize()"
          [first]="tableFirst()"
          [totalRecords]="totalRecords()"
          [loading]="loading()"
          [rowsPerPageOptions]="[10, 20, 50]"
          (onLazyLoad)="onPageChange($event)"
          dataKey="id"
          scrollable="true"
          scrollHeight="300px"
          styleClass="allocation-table user-quota-table">
          <ng-template pTemplate="header">
            <tr>
              <th>User</th>
              <th class="usage-col">Usage</th>
              <th class="datetime-col">Allocation date</th>
              <th class="created-by-col">Allocated by</th>
              <th class="actions-col">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            @let usage = userUsage(row);
            <tr>
              <td>
                <div class="user-cell">
                  <span class="user-name">{{ userRowLabel(row) }}</span>
                  @if (row.userEmail) {
                    <span class="user-email">{{ row.userEmail }}</span>
                  }
                </div>
              </td>
              <td class="usage-col">
                <div class="token-usage-inline">
                  <div
                    class="token-usage-bar token-usage-bar--compact"
                    role="progressbar"
                    [attr.aria-valuenow]="usage.usedPercent"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    [attr.aria-label]="userRowLabel(row) + ': ' + usage.usedPercent + '% consumed'">
                    <div
                      class="token-usage-bar__fill"
                      [class]="'token-usage-bar__fill--' + consumedBarTone(usage.usedPercent)"
                      [style.width.%]="usage.usedPercent"></div>
                  </div>
                  <div class="token-usage-inline__labels">
                    <span class="consumed">{{ usage.usedPercent }}% Consumed</span>
                    <span class="available">{{ usage.remainingPercent }}% Available</span>
                  </div>
                </div>
              </td>
              <td class="datetime-col">{{ formatQuotaDateTime(row.allocationDate) }}</td>
              <td class="created-by-col">
                <span [pTooltip]="row.createdBy || undefined" tooltipPosition="top">{{ row.createdBy || '—' }}</span>
              </td>
              <td class="actions-col">
                <div class="quota-row-actions">
                  <button
                    type="button"
                    class="quota-icon-btn"
                    pTooltip="Usage metrics"
                    tooltipPosition="top"
                    aria-label="Usage metrics"
                    (click)="openUserMetrics(row)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <use xlink:href="assets/images/icon-library.svg#chart-pie"></use>
                    </svg>
                  </button>
                  @if (isActiveQuotaView()) {
                    <button
                      type="button"
                      class="quota-icon-btn"
                      pTooltip="Add tokens"
                      tooltipPosition="top"
                      aria-label="Add tokens"
                      (click)="openAddTokens(row)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <use xlink:href="assets/images/icon-library.svg#plus"></use>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="quota-icon-btn"
                      pTooltip="Edit allocation"
                      tooltipPosition="top"
                      aria-label="Edit allocation"
                      (click)="openEdit(row)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <use xlink:href="assets/images/icon-library.svg#pencil"></use>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="quota-icon-btn quota-icon-btn--danger"
                      pTooltip="Remove allocation"
                      tooltipPosition="top"
                      aria-label="Remove allocation"
                      (click)="confirmDelete(row)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <use xlink:href="assets/images/icon-library.svg#trash"></use>
                      </svg>
                    </button>
                  } @else {
                    <button
                      type="button"
                      class="quota-icon-btn"
                      pTooltip="Restore allocation"
                      tooltipPosition="top"
                      aria-label="Restore allocation"
                      [disabled]="restoringQuotaId() === row.id"
                      (click)="restoreQuota(row)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <use xlink:href="assets/images/icon-library.svg#rotate-ccw"></use>
                      </svg>
                    </button>
                  }
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="5" class="empty-cell">{{ quotaEmptyMessage() }}</td>
            </tr>
          </ng-template>
        </p-table>
      }
    </section>
  }
</p-drawer>

<p-dialog
  [visible]="formVisible()"
  (visibleChange)="formVisible.set($event)"
  [modal]="true"
  appendTo="body"
  [baseZIndex]="12000"
  [style]="{ width: '440px' }"
  styleClass="tokens-allocation-form-dialog"
  [header]="formMode === 'create' ? 'Assign' : 'Update User Tokens'"
  (onHide)="closeForm()">
  <div class="quota-form">
    @if (formMode === 'create') {
      <label class="field-label">User</label>
      <p-select
        [options]="assignableUserOptions()"
        optionLabel="label"
        optionValue="id"
        [ngModel]="formSelectedUserId"
        (ngModelChange)="formSelectedUserId = $event"
        [loading]="assignableUsersLoading()"
        placeholder="Select user"
        [filter]="true"
        filterPlaceholder="Search users"
        styleClass="w-full"
        appendTo="body" />
    } @else {
      <label class="field-label">User</label>
      <p class="field-readonly">{{ formUserLabel }}</p>
    }

    <label class="field-label">Renewal type</label>
    <p-select
      [options]="renewalTypeOptions"
      optionLabel="label"
      optionValue="value"
      [ngModel]="formRenewalType"
      (ngModelChange)="formRenewalType = $event"
      styleClass="w-full"
      appendTo="body" />

    @if (showFormRenewalOn()) {
      <label class="field-label">Renewal on</label>
      <p-select
        [options]="formRenewalOnOptions()"
        optionLabel="label"
        optionValue="value"
        [ngModel]="formRenewalOn"
        (ngModelChange)="formRenewalOn = $event"
        styleClass="w-full"
        appendTo="body" />
    }

    <div class="renewal-status-row">
      <label class="field-label renewal-status-row__label">Renewal status</label>
      <p-toggleswitch [(ngModel)]="formRenewalStatusOn" />
    </div>

    @if (formMode === 'edit') {
      <label class="field-label">Used tokens</label>
      <p class="field-readonly">{{ formatTokens(formUsedTokens) }}</p>
    }

    <label class="field-label">Allocated tokens</label>
    <p-inputNumber [(ngModel)]="formAllocatedTokens" [min]="0" [useGrouping]="true" inputStyleClass="w-full" />
    @if (formAllocationError(); as allocationError) {
      <p class="field-error" role="alert">{{ allocationError }}</p>
    }

    @if (formMode === 'edit') {
      <label class="field-label">Renewal tokens</label>
      <p-inputNumber [(ngModel)]="formRenewalTokens" [min]="0" [useGrouping]="true" inputStyleClass="w-full" />
    }
  </div>
  <ng-template pTemplate="footer">
    <button type="button" pButton severity="secondary" label="Cancel" (click)="closeForm()"></button>
    <button
      type="button"
      pButton
      [label]="formMode === 'create' ? 'Assign' : 'Save'"
      [loading]="formSaving()"
      [disabled]="!canSubmitForm()"
      (click)="saveForm()"></button>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="addTokensVisible()"
  (visibleChange)="onAddTokensVisibleChange($event)"
  [modal]="true"
  appendTo="body"
  [baseZIndex]="12000"
  [style]="{ width: '420px' }"
  styleClass="tokens-allocation-form-dialog"
  header="Add Tokens"
  (onHide)="closeAddTokens()">
  <div class="quota-form">
    <div class="quota-detail-row">
      <span class="quota-detail-row__label">User</span>
      <span class="quota-detail-row__value">{{ addTokensUserLabel }}</span>
    </div>
    <div class="quota-detail-row">
      <span class="quota-detail-row__label">Allocated tokens</span>
      <span class="quota-detail-row__value">{{ addTokensAllocatedLabel }}</span>
    </div>
    <div class="quota-detail-row">
      <span class="quota-detail-row__label">Renewal type</span>
      <span class="quota-detail-row__value">{{ addTokensRenewalTypeLabel }}</span>
    </div>
    <div class="quota-detail-row">
      <span class="quota-detail-row__label">Renewal on</span>
      <span class="quota-detail-row__value">{{ addTokensRenewalOnLabel }}</span>
    </div>

    <label class="field-label">Add tokens</label>
    <p-inputNumber [(ngModel)]="addTokensAmount" [min]="0" [useGrouping]="true" inputStyleClass="w-full" />
  </div>
  <ng-template pTemplate="footer">
    <button type="button" pButton severity="secondary" label="Cancel" (click)="closeAddTokens()"></button>
    <button
      type="button"
      pButton
      label="Add"
      [loading]="addTokensSaving()"
      (click)="saveAddTokens()"></button>
  </ng-template>
</p-dialog>
